import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { api } from '../services/api';
import RazorpayCheckout from '../components/payment/RazorpayCheckout';
import { getCurrencySymbol } from '../services/razorpay';
import * as Sentry from "@sentry/react";
import { ChevronRight, DollarSign, CreditCard, AlertCircle, X, Check } from 'lucide-react';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';

import './CreditsModal.css';

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'credits-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'credits-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'credits-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Security: Safe error messages mapping
const ERROR_MESSAGES = {
  // Authentication & User Profile
  'MISSING_USER_PROFILE': 'User profile information missing. Please refresh and try again',
  'INCOMPLETE_PROFILE': 'Please complete your profile before making a payment',
  'PROFILE_FETCH_FAILED': 'Unable to load profile settings. Please refresh the page',
  'RATE_LIMIT_EXCEEDED': 'Too many requests. Please wait 60 seconds before trying again',

  // System & Service
  'SERVICE_CONFIG_ERROR': 'Payment service temporarily unavailable. Please try again later',
  'DB_ERROR': 'Service temporarily unavailable. Please try again later',
  'CURRENCY_ERROR': 'Currency processing error. Please try again later',
  'INTERNAL_ERROR': 'Service temporarily unavailable. Please try again later',
  
  // Payment Processing
  'PAYMENT_API_ERROR': 'Payment service error. Please try again later',
  'DUPLICATE_ORDER': 'Order already exists. Please refresh and try again',
  'PAYMENT_CREATION_FAILED': 'Unable to create payment. Please try again',
  'PAYMENT_PROCESSING_ERROR': 'Payment processing failed. Please try again',
  
  // Input Validation
  'MISSING_AMOUNT': 'Amount is required',
  'INVALID_AMOUNT': 'Please enter a valid amount',
  'BELOW_MINIMUM_AMOUNT': 'Amount does not meet minimum requirement',
  
  // Client-side Errors
  'NETWORK_ERROR': 'Network error. Please check your connection and try again',
  
  // Additional payment-specific errors
'INSUFFICIENT_CREDITS': 'Insufficient credits. Please add funds to your account.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  'ABOVE_MAXIMUM_AMOUNT': 'Amount exceeds maximum card payment limit. For larger amounts, contact support@barrack.ai for bank transfer payment.',  // Add to ERROR_MESSAGES object in both files
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
};

// Performance: Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Amount validation function
const validateAmount = (amount, currency, minimumAmounts, maximumAmounts) => {
  if (!amount || typeof amount !== 'string') return { valid: false, error: 'Amount is required' };
  
  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount)) return { valid: false, error: 'Please enter a valid amount' };
  if (numericAmount <= 0) return { valid: false, error: 'Amount must be greater than zero' };
  if (numericAmount > 1000000) return { valid: false, error: 'Amount exceeds maximum limit' };
  
  const minAmount = minimumAmounts[currency] || 0;
  if (numericAmount < minAmount) {
    return { valid: false, error: `Minimum amount for ${currency} is ${getCurrencySymbol(currency)}${minAmount}` };
  }
  
  const maxAmount = maximumAmounts ? maximumAmounts[currency] : null;
  if (maxAmount && numericAmount > maxAmount) {
    return { valid: false, error: `Maximum card payment for ${currency} is ${getCurrencySymbol(currency)}${maxAmount.toLocaleString()}. For larger amounts, contact support@barrack.ai for bank transfer payment.` };
  }
  
  return { valid: true };
};
// Security: Safe error message function
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return ERROR_MESSAGES['INTERNAL_ERROR'];
  }
  
  // 1. Check error.error property first (interceptor's user-friendly message)
  if (error.error && typeof error.error === 'string') {
    return sanitizeText(error.error);
  }
  
  // 2. Check direct error code
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // 3. Handle HTTP status codes
  if (error.status === 401) {
    return 'Please log in again to continue';
  }
  
  // 4. Fallback to sanitized message if available
  if (error.message && typeof error.message === 'string') {
    return sanitizeText(error.message);
  }
  
  return ERROR_MESSAGES['INTERNAL_ERROR'];
};// Security: Validate error object structure
const validateErrorObject = (error) => {
  if (!error || typeof error !== 'object') return false;
  return true;
};

// Responsive utilities
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getModalClasses = () => {
    if (isMobile) return 'modal-content credits-modal mobile';
    if (isTablet) return 'modal-content credits-modal tablet';
    return 'modal-content credits-modal';
  };

  const getPackagesGridClasses = () => {
    if (isMobile) return 'packages-grid mobile';
    if (isTablet) return 'packages-grid tablet';
    return 'packages-grid';
  };

  const getModalActionsClasses = () => {
    if (isMobile) return 'modal-actions mobile';
    if (isTablet) return 'modal-actions tablet';
    return 'modal-actions';
  };

  return { isMobile, isTablet, getModalClasses, getPackagesGridClasses, getModalActionsClasses };
};

const CreditsModal = ({ isOpen, onClose, updateBalance }) => {
  const { isMobile, isTablet, getModalClasses, getPackagesGridClasses, getModalActionsClasses } = useResponsive();
  const { handleAuthError } = useAuth();
  
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState(null);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [activePackage, setActivePackage] = useState(null);
  const [razorpayData, setRazorpayData] = useState(null);
  const [orderCreationLocked, setOrderCreationLocked] = useState(false);
  const navigate = useNavigate();

  // Performance: Controlled loading states
  const [loadingStates, setLoadingStates] = useState({
    profile: false,
    payment: false,
    processing: false
  });

  // Performance: Initial load tracking
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Performance: Request cancellation
  const abortControllerRef = useRef(null);
  const paymentTimeoutRef = useRef(null);
  const orderCreationInProgressRef = useRef(false);
  const orderCreationAttemptsRef = useRef(0);
  const MAX_ORDER_ATTEMPTS = 3;

  // Security: Rate limiting
  const lastRequestTimeRef = useRef(0);
  const REQUEST_COOLDOWN = 2000;

  // Component mount logging
  useEffect(() => {
    if (isOpen) {
      logInfo('CreditsModal opened', {
        isMobile,
        isTablet,
        initialCurrency: currency
      });
      
      logUserAction('credits_modal_opened', {
        isMobile,
        isTablet
      });
    }
  }, [isOpen, isMobile, isTablet, currency]);

  // Reset order creation attempts after 5 minutes
  useEffect(() => {
    const resetTimer = setInterval(() => {
      orderCreationAttemptsRef.current = 0;
      logInfo('Order creation attempts counter reset');
    }, 300000);
    
    return () => clearInterval(resetTimer);
  }, []);

  // Set Sentry context
  useEffect(() => {
    if (isOpen) {
      Sentry.setContext("credits-modal", {
        isOpen,
        currency,
        amount: amount ? parseFloat(amount) : 0,
        activePackage,
        showCheckoutForm,
        isMobile,
        isTablet,
        hasError: !!error,
        loadingStates
      });
    }
  }, [isOpen, currency, amount, activePackage, showCheckoutForm, isMobile, isTablet, error, loadingStates]);

  // Define minimum amounts for each currency
const minimumAmounts = useMemo(() => ({
    'USD': 5,
    'EUR': 5,
    'INR': 100
  }), []);

  const maximumAmounts = useMemo(() => ({
    'USD': 2000,
    'EUR': 1500,
    'INR': 400000
  }), []);

  // Performance: Memoized currency-specific packages
  const packages = useMemo(() => {
    const packageData = {
      'INR': [
        { id: 'starter', name: 'Starter', amount: 1000, description: 'Get started quickly' },
        { id: 'light', name: 'Light', amount: 5000, description: 'Ideal for hourly runs' },
        { id: 'regular', name: 'Regular', amount: 20000, description: 'For day-long sessions' },
      ],
      'USD': [
        { id: 'starter', name: 'Starter', amount: 100, description: 'Get started quickly' },
        { id: 'light', name: 'Light', amount: 500, description: 'Ideal for hourly runs' },
        { id: 'regular', name: 'Regular', amount: 2000, description: 'For day-long sessions' },
      ],
      'EUR': [
        { id: 'starter', name: 'Starter', amount: 100, description: 'Get started quickly' },
        { id: 'light', name: 'Light', amount: 500, description: 'Ideal for hourly runs' },
        { id: 'regular', name: 'Regular', amount: 2000, description: 'For day-long sessions' },
      ]
    };

    const selectedPackages = packageData[currency] || packageData['USD'];
    
    logInfo('Packages updated for currency', {
      currency,
      packagesCount: selectedPackages.length,
      packages: selectedPackages.map(p => ({ id: p.id, amount: p.amount }))
    });

    return selectedPackages;
  }, [currency]);

  // Performance: Memoized tax calculation
  const shouldShowTax = useCallback(() => currency === 'INR', [currency]);

  // Performance: Memoized calculations
  const calculatedValues = useMemo(() => {
    const numericAmount = amount ? parseFloat(amount) : 0;
    const baseAmount = isNaN(numericAmount) ? 0 : numericAmount;
    const taxAmount = shouldShowTax() ? baseAmount * 0.18 : 0;
    const totalAmount = baseAmount + taxAmount;

    return {
      baseAmount,
      taxAmount,
      totalAmount,
      formattedBase: baseAmount.toFixed(2),
      formattedTax: taxAmount.toFixed(2),
      formattedTotal: totalAmount.toFixed(2)
    };
  }, [amount, shouldShowTax]);

// Fetch user's profile to check completeness and get currency
const fetchUserProfile = useCallback(async () => {
  const startTime = performance.now();
  
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  
  abortControllerRef.current = new AbortController();
  setLoadingStates(prev => ({ ...prev, profile: true }));
  setError(null);

  logInfo('Fetching user profile for payment setup', {
    currentCurrency: currency
  });

  try {
    const profileResponse = await api.getUserProfile();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (validateErrorObject(profileResponse)) {
      if (profileResponse.success && profileResponse.profile) {
        const isComplete = profileResponse.profile.is_profile_complete || false;
        
        if (!isComplete) {
          logUserAction('profile_incomplete_redirect', {
            duration: `${duration.toFixed(2)}ms`
          });
          
          if (typeof onClose === 'function') {
            onClose();
          }
          
          navigate(`/account?from=${encodeURIComponent(window.location.pathname)}`);
          return;
        }
        
        if (profileResponse.profile.preferred_currency) {
          const newCurrency = sanitizeText(profileResponse.profile.preferred_currency);
          setCurrency(newCurrency);
          setAmount('');
          setActivePackage(null);
          
          if (!initialDataLoaded) setInitialDataLoaded(true);
          
          logPerformance('fetch_user_profile', duration, {
            success: true,
            profileComplete: true,
            currency: newCurrency
          });
          
          logInfo('User profile loaded successfully', {
            duration: `${duration.toFixed(2)}ms`,
            currency: newCurrency,
            profileComplete: isComplete
          });
          
          logUserAction('profile_loaded_currency_set', {
            currency: newCurrency,
            duration: `${duration.toFixed(2)}ms`
          });
        }
      } else {
        const safeMessage = getSafeErrorMessage(profileResponse);
        setError(safeMessage);
        
        logPerformance('fetch_user_profile', duration, {
          success: false,
          error: safeMessage
        });
        
        logError('Profile fetch failed', {
          duration: `${duration.toFixed(2)}ms`,
          response: profileResponse
        });
      }
    }
 } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (err.name === 'AbortError') {
      logInfo('Profile fetch aborted by user');
      return;
    }
    
    // Position 1: Check 401
    if (err.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (err.code) {
      const safeMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['PROFILE_FETCH_FAILED'];
      setError(safeMessage);
      setCurrency('USD');
      
      logPerformance('fetch_user_profile', duration, {
        success: false,
        error: safeMessage,
        errorCode: err.code
      });
      
      logError('Profile fetch error', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      
      logUserAction('profile_fetch_error', {
        errorCode: err.code,
        fallbackCurrency: 'USD',
        duration: `${duration.toFixed(2)}ms`
      });
      return;
    }
    
    // Position 3: Fallback
    const safeMessage = err.error || ERROR_MESSAGES['PROFILE_FETCH_FAILED'];
    setError(safeMessage);
    setCurrency('USD');
    
    logPerformance('fetch_user_profile', duration, {
      success: false,
      error: safeMessage
    });
    
    logError('Profile fetch error - fallback', {
      duration: `${duration.toFixed(2)}ms`
    }, err);
    
    logUserAction('profile_fetch_error_fallback', {
      fallbackCurrency: 'USD',
      duration: `${duration.toFixed(2)}ms`
    });
  } finally {
    setLoadingStates(prev => ({ ...prev, profile: false }));
  }
}, [currency, initialDataLoaded, onClose, navigate, handleAuthError]);
  // Performance: Debounced fetch function
  const debouncedFetchUserProfile = useMemo(
    () => debounce(async () => {
      await fetchUserProfile();
    }, 300),
    [fetchUserProfile]
  );

  // Focus/Visibility refresh for cross-tab synchronization
  useEffect(() => {
    const handleFocus = () => {
      if (initialDataLoaded && isOpen) {
        debouncedFetchUserProfile();
        logInfo('Refreshed profile data on page focus');
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden && initialDataLoaded && isOpen) {
        debouncedFetchUserProfile();
        logInfo('Refreshed profile data on visibility change');
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [initialDataLoaded, debouncedFetchUserProfile, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    fetchUserProfile();
  }, [isOpen, fetchUserProfile]);

  // Reset form when modal opens/closes - FIX 4: Reset refs on modal open
  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setError(null);
      setShowCheckoutForm(false);
      setActivePackage(null);
      setRazorpayData(null);
      setOrderCreationLocked(false);
      orderCreationAttemptsRef.current = 0;
      orderCreationInProgressRef.current = false;
      setLoadingStates({
        profile: false,
        payment: false,
        processing: false
      });
      
      logInfo('Credits modal form reset', {
        isOpen
      });
    } else {
      logInfo('CreditsModal closed', {
        hadAmount: !!amount,
        hadActivePackage: !!activePackage,
        wasInCheckout: showCheckoutForm
      });
      
      logUserAction('credits_modal_closed', {
        hadAmount: !!amount,
        hadActivePackage: !!activePackage,
        wasInCheckout: showCheckoutForm
      });
    }
  }, [isOpen]);

  // Performance: Request cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
      }
      
      logInfo('CreditsModal component unmounted', {
        hadPendingRequests: !!(abortControllerRef.current || paymentTimeoutRef.current)
      });
    };
  }, []);

  const handlePaymentSuccess = useCallback(() => {
    logUserAction('payment_completed_successfully', {
      amount: amount ? parseFloat(amount) : 0,
      currency,
      activePackage
    });
    
    logInfo('Payment completed and balance update triggered', {
      amount: amount ? parseFloat(amount) : 0,
      currency,
      activePackage
    });
    
    if (typeof updateBalance === 'function') {
      updateBalance();
    }
    onClose();
  }, [updateBalance, onClose, amount, currency, activePackage]);

  // Security: Rate limiting check
  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeRef.current;
    
    if (timeSinceLastRequest < REQUEST_COOLDOWN) {
      logUserAction('payment_rate_limited', {
        timeSinceLastRequest,
        cooldownPeriod: REQUEST_COOLDOWN
      });
      return false;
    }
    
    lastRequestTimeRef.current = now;
    return true;
  }, []);

const handleProceedToPayment = useCallback(async () => {
  // FIX 1: Reset ref before returning on duplicate detection
  if (orderCreationInProgressRef.current) {
    logInfo('Order creation already in progress, ignoring duplicate request');
    orderCreationInProgressRef.current = false;
    return;
  }
  
  orderCreationInProgressRef.current = true;
  
  const startTime = performance.now();
  
  logUserAction('payment_process_initiated', {
    amount: amount ? parseFloat(amount) : 0,
    currency,
    activePackage
  });
  
  // Security: Rate limiting
  if (!checkRateLimit()) {
    orderCreationInProgressRef.current = false;
    const safeMessage = getSafeErrorMessage({ code: 'RATE_LIMIT_EXCEEDED' });
    setError(safeMessage);
    return;
  }

  // Check attempt limit
  if (orderCreationAttemptsRef.current >= MAX_ORDER_ATTEMPTS) {
    orderCreationInProgressRef.current = false;
    setError('Maximum order creation attempts reached. Please wait 5 minutes or refresh the page.');
    logError('Order creation attempt limit reached', {
      attempts: orderCreationAttemptsRef.current,
      maxAttempts: MAX_ORDER_ATTEMPTS
    });
    return;
  }
  
  orderCreationAttemptsRef.current++;
  logInfo('Order creation attempt', { 
    attemptNumber: orderCreationAttemptsRef.current,
    maxAttempts: MAX_ORDER_ATTEMPTS 
  });

  // Security: Enhanced validation
const validation = validateAmount(amount, currency, minimumAmounts, maximumAmounts);
  if (!validation.valid) {
    orderCreationInProgressRef.current = false;
    setError(sanitizeText(validation.error));
    
    logUserAction('payment_validation_failed', {
      validationError: validation.error,
      amount: amount ? parseFloat(amount) : 0,
      currency
    });
    
    logInfo('Payment validation failed', {
      validationError: validation.error,
      amount: amount ? parseFloat(amount) : 0,
      currency
    });
    return;
  }
 
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  
  abortControllerRef.current = new AbortController();
  setLoadingStates(prev => ({ ...prev, payment: true, processing: true }));
  setError(null);
 
  // Clear any existing timeout
  if (paymentTimeoutRef.current) {
    clearTimeout(paymentTimeoutRef.current);
  }

  // Set payment timeout
  paymentTimeoutRef.current = setTimeout(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const safeMessage = getSafeErrorMessage({ code: 'PAYMENT_PROCESSING_ERROR' });
    setError(safeMessage);
    setLoadingStates(prev => ({ ...prev, payment: false, processing: false }));
    
    logError('Payment creation timeout', {
      amount: amount ? parseFloat(amount) : 0,
      currency,
      timeoutDuration: 30000
    });
  }, 30000);

  try {
    const baseAmount = parseFloat(amount);
    
    logInfo('Payment creation API call started', {
      baseAmount,
      currency
    });
    
    const response = await api.createPaymentWithProfile(
      baseAmount,
      currency
    );
    
    const endTime = performance.now();
    const duration = endTime - startTime;
   
    if (validateErrorObject(response)) {
      if (response.success) {
        // Reset attempt counter on success
        orderCreationAttemptsRef.current = 0;
        
        setRazorpayData(response);
        setShowCheckoutForm(true);
        
        // Lock "Proceed to Payment" button for 3 seconds
        setOrderCreationLocked(true);
        setTimeout(() => {
          setOrderCreationLocked(false);
        }, 3000);
        
        logPerformance('create_payment', duration, {
          success: true,
          amount: baseAmount,
          currency,
          orderId: response.order_id
        });
        
        logInfo('Payment created successfully', {
          duration: `${duration.toFixed(2)}ms`,
          amount: baseAmount,
          currency,
          orderId: response.order_id
        });
        
        logUserAction('payment_created_success', {
          amount: baseAmount,
          currency,
          orderId: response.order_id,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        let safeMessage;
        
        if (response.error === 'incomplete_profile') {
          safeMessage = getSafeErrorMessage({ code: 'INCOMPLETE_PROFILE' });
          
          if (response.redirect_to) {
            if (typeof onClose === 'function') {
              onClose();
            }
            
            logUserAction('payment_profile_incomplete_redirect', {
              redirectTo: response.redirect_to,
              duration: `${duration.toFixed(2)}ms`
            });
            
            setTimeout(() => {
              navigate(sanitizeText(response.redirect_to));
            }, 1000);
          }
        } else if (response.min_amount) {
          safeMessage = `Minimum amount for ${currency} is ${getCurrencySymbol(currency)}${response.min_amount}`;
          
          logUserAction('payment_minimum_amount_error', {
            providedAmount: baseAmount,
            minimumAmount: response.min_amount,
            currency
          });
        } else {
          safeMessage = getSafeErrorMessage(response);
        }
        
        setError(safeMessage);
        
        logPerformance('create_payment', duration, {
          success: false,
          error: safeMessage,
          errorCode: response.error,
          amount: baseAmount,
          currency
        });
        
        logError('Payment creation failed', {
          duration: `${duration.toFixed(2)}ms`,
          error: safeMessage,
          errorCode: response.error,
          amount: baseAmount,
          currency
        });
        
        logUserAction('payment_creation_failed', {
          error: safeMessage,
          errorCode: response.error,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } else {
      const safeMessage = getSafeErrorMessage({ code: 'PAYMENT_CREATION_FAILED' });
      setError(safeMessage);
      
      logPerformance('create_payment', duration, {
        success: false,
        error: safeMessage,
        amount: baseAmount,
        currency
      });
      
      logError('Payment creation failed - invalid response', {
        duration: `${duration.toFixed(2)}ms`,
        amount: baseAmount,
        currency
      });
      
      logUserAction('payment_creation_invalid_response', {
        duration: `${duration.toFixed(2)}ms`
      });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (err.name === 'AbortError') {
      logInfo('Payment creation aborted by user');
      return;
    }
    
    // 1. Check 401
    if (err.status === 401) {
      handleAuthError();
      return;
    }
    
    // 2. Check error code and use interceptor's message
    if (err.code) {
      const safeMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['PAYMENT_CREATION_FAILED'];
      setError(safeMessage);
      
      logPerformance('create_payment', duration, {
        success: false,
        error: safeMessage,
        errorCode: err.code,
        status: err.status
      });
      
      logError('Order creation error', {
        code: err.code,
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      
      logUserAction('order_creation_failed', {
        errorCode: err.code,
        duration: `${duration.toFixed(2)}ms`
      });
      return;
    }
    
    // 3. Fallback
    const safeMessage = err.error || ERROR_MESSAGES['PAYMENT_CREATION_FAILED'];
    setError(safeMessage);
    
    logPerformance('create_payment', duration, {
      success: false,
      error: safeMessage
    });
    
    logError('Payment creation error - fallback', {
      duration: `${duration.toFixed(2)}ms`,
      amount: amount ? parseFloat(amount) : 0,
      currency
    }, err);
    
    logUserAction('payment_creation_error', {
      duration: `${duration.toFixed(2)}ms`
    });
  } finally {
    // FIX 5: Reset ref in finally block
    orderCreationInProgressRef.current = false;
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
    }
    setLoadingStates(prev => ({ ...prev, payment: false, processing: false }));
  }
}, [amount, currency, minimumAmounts, checkRateLimit, onClose, navigate, activePackage, handleAuthError]);



  const selectPackage = useCallback((pkg) => {
    setActivePackage(pkg.id);
    setAmount(pkg.amount.toString());
    setError(null);
    
    logUserAction('package_selected', {
      packageId: pkg.id,
      packageName: pkg.name,
      amount: pkg.amount,
      currency
    });
    
    logInfo('Package selected', {
      packageId: pkg.id,
      packageName: pkg.name,
      amount: pkg.amount,
      currency
    });
  }, [currency]);

  // Performance: Memoized number formatting
  const formatNumber = useCallback((value, places = 2) => {
    if (!value) return '0.00';
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(numericValue) ? '0.00' : numericValue.toFixed(places);
  }, []);

  // Security: Enhanced amount input handling
  const handleAmountChange = useCallback((e) => {
    const input = e.target.value;
    
    // Security: Length limit
    if (input.length > 20) {
      logUserAction('amount_input_length_exceeded', {
        inputLength: input.length,
        maxLength: 20
      });
      return;
    }
    
    let sanitizedValue = input.replace(/[^\d.]/g, '');
    
    const decimalPoints = sanitizedValue.match(/\./g) || [];
    if (decimalPoints.length > 1) {
      const parts = sanitizedValue.split('.');
      sanitizedValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    if (sanitizedValue.includes('.')) {
      const parts = sanitizedValue.split('.');
      if (parts[1] && parts[1].length > 2) {
        sanitizedValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
    }
    
    if (sanitizedValue.length > 1 && sanitizedValue[0] === '0' && sanitizedValue[1] !== '.') {
      sanitizedValue = sanitizedValue.substring(1);
    }
    
    setAmount(sanitizedValue);
    setActivePackage(null);
    
    if (error && error.includes('Minimum amount')) {
      setError(null);
    }
    
    logUserAction('amount_changed', {
      newAmount: sanitizedValue ? parseFloat(sanitizedValue) : 0,
      currency,
      isCustomAmount: true
    });
  }, [error, currency]);

  const handleAmountBlur = useCallback(() => {
    if (!amount) {
      setAmount('');
      return;
    }
    
    try {
      const numericAmount = parseFloat(amount);
      if (!isNaN(numericAmount)) {
        const formattedAmount = numericAmount.toFixed(2);
        setAmount(formattedAmount);
        
        logUserAction('amount_formatted', {
          originalAmount: amount,
          formattedAmount,
          currency
        });
      } else {
        setAmount('');
        logUserAction('amount_cleared_invalid', {
          originalAmount: amount
        });
      }
    } catch (e) {
      setAmount('');
      logUserAction('amount_cleared_error', {
        originalAmount: amount,
        error: e.message
      });
    }
  }, [amount, currency]);

  // FIX 3: Clear razorpay data and lock state on back navigation
  const handleCheckoutBack = useCallback(() => {
    setShowCheckoutForm(false);
    setRazorpayData(null);
    setOrderCreationLocked(false);
    
    logUserAction('checkout_back_to_selection', {
      amount: amount ? parseFloat(amount) : 0,
      currency
    });
    
    logInfo('User returned from checkout to amount selection', {
      amount: amount ? parseFloat(amount) : 0,
      currency
    });
  }, [amount, currency]);

  const handleModalClose = useCallback(() => {
    logUserAction('credits_modal_manual_close', {
      hadAmount: !!amount,
      hadActivePackage: !!activePackage,
      wasInCheckout: showCheckoutForm
    });
    
    onClose();
  }, [onClose, amount, activePackage, showCheckoutForm]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className={getModalClasses()}>
        <div className={`modal-header ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
          <h3>Add Credits</h3>
          <button className="close-button" onClick={handleModalClose}>
            <X size={isMobile ? 18 : 20} />
          </button>
        </div>

        {!showCheckoutForm ? (
          <div className={`modal-body ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
            <div className={`currency-info ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
              <DollarSign size={isMobile ? 14 : 16} />
              <span>
                Based on your location, payment will be processed in <span className="currency-highlight">{sanitizeText(currency)}</span>.
                {shouldShowTax() && " GST will be added at checkout."}
              </span>
            </div>

            <div className="package-selection">
              <h4>Common Top-Up Amounts</h4>
              <div className={getPackagesGridClasses()}>
                {packages.map(pkg => (
                  <div 
                    key={pkg.id} 
                    className={`package-card border border-[rgba(202,217,227,0.3)] ${activePackage === pkg.id ? 'active' : ''} ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}
                    onClick={() => selectPackage(pkg)}
                  >
                    <div className="package-header">
                      <div className="package-name">{sanitizeText(pkg.name)}</div>
                      {activePackage === pkg.id && <div className="selected-indicator"><Check size={isMobile ? 12 : 14} /></div>}
                    </div>
                    <div className="package-amount">
                      {getCurrencySymbol(currency)}{pkg.amount}
                    </div>
                    <div className="package-description">{sanitizeText(pkg.description)}</div>
                  </div>
                ))}
              </div>
              
              <div className={`custom-amount ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                <h4>Custom Amount</h4>
                <div className="amount-input-container">
                  <div className="currency-symbol">{getCurrencySymbol(currency)}</div>
                  <input
                    type="text"
                    value={amount}
                    onChange={handleAmountChange}
                    onBlur={handleAmountBlur}
                    placeholder="Enter custom amount"
                    className="amount-input"
                    aria-label={`Amount in ${currency}`}
                    inputMode="decimal"
                    maxLength="20"
                    disabled={loadingStates.profile || loadingStates.processing}
                  />
                </div>
                <small className="input-hint">1 {sanitizeText(currency)} = 1 Credit</small>
<small className="input-hint min-amount">Minimum amount: {getCurrencySymbol(currency)}{minimumAmounts[currency] || 0}</small>
                <small className="input-hint max-amount">Maximum card payment: {getCurrencySymbol(currency)}{(maximumAmounts[currency] || 0).toLocaleString()}</small>
                              </div>
            </div>

            <div className={`summary-card ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
              <h4>Payment Summary</h4>
              <div className="summary-row">
                <span className="summary-label">Base Amount:</span>
                <span className="summary-value">
                  {getCurrencySymbol(currency)}
                  {formatNumber(calculatedValues.formattedBase)}
                </span>
              </div>
              
              {shouldShowTax() && (
                <div className="summary-row">
                  <span className="summary-label">GST (18%):</span>
                  <span className="summary-value tax">
                    {amount ? getCurrencySymbol(currency) + formatNumber(calculatedValues.formattedTax) : 'Calculated at checkout'}
                  </span>
                </div>
              )}
              
              <div className="summary-divider"></div>
              <div className="summary-row total">
                <span className="summary-label">Total:</span>
                <span className="summary-value total-value">
                  {getCurrencySymbol(currency)}
                  {formatNumber(calculatedValues.formattedTotal)}
                </span>
              </div>
            </div>

            {error && (
              <div className={`error-message ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                <AlertCircle size={isMobile ? 14 : 16} />
                <p>{sanitizeText(error)}</p>
              </div>
            )}

            <div className={getModalActionsClasses()}>
              <button
                className="modal-button cancel"
                onClick={handleModalClose}
                disabled={loadingStates.processing}
              >
                Cancel
              </button>
              <button
                className="modal-button proceed"
                onClick={handleProceedToPayment}
                disabled={
                  !amount || 
                  loadingStates.processing || 
                  parseFloat(amount) <= 0 || 
                  loadingStates.profile ||
                  orderCreationLocked
                }
              >
                {loadingStates.processing ? 'Processing...' : (
                  <>
                    <span>Proceed to Payment</span>
                    <ChevronRight size={isMobile ? 14 : 16} />
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className={`checkout-container ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
            <div className={`checkout-payment-summary ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
              <h4>Payment Summary</h4>
              <div className="summary-row">
                <span className="summary-label">Base Amount:</span>
                <span className="summary-value">{razorpayData ? sanitizeText(razorpayData.currency_symbol) : getCurrencySymbol(currency)}{formatNumber(razorpayData ? razorpayData.amount : amount)}</span>
              </div>
              
              {shouldShowTax() && (razorpayData ? razorpayData.tax_amount > 0 : false) && (
                <div className="summary-row">
                  <span className="summary-label">GST (18%):</span>
                  <span className="summary-value">{razorpayData ? sanitizeText(razorpayData.currency_symbol) : getCurrencySymbol(currency)}{formatNumber(razorpayData ? razorpayData.tax_amount : 0)}</span>
                </div>
              )}
              
              <div className="summary-divider"></div>
              <div className="summary-row total">
                <span className="summary-label">Total Amount:</span>
                <span className="summary-value total-value">{razorpayData ? sanitizeText(razorpayData.currency_symbol) : getCurrencySymbol(currency)}{formatNumber(razorpayData ? razorpayData.total_amount : amount)}</span>
              </div>
            </div>
            
            <div className={`checkout-actions-container ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
              <div className={`checkout-disclaimer ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                By purchasing credits, you acknowledge that credits are non-refundable and cannot be withdrawn as cash.
              </div>
              
              <div className={`payment-actions ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
                <button 
                  className="modal-button cancel"
                  onClick={handleCheckoutBack}
                  disabled={loadingStates.processing}
                >
                  Cancel
                </button>
                
                <RazorpayCheckout
                  amount={razorpayData ? razorpayData.total_amount : parseFloat(amount)}
                  currency={currency}
                  orderId={razorpayData ? sanitizeText(razorpayData.order_id) : null}
                  keyId={razorpayData ? sanitizeText(razorpayData.key_id) : null}
                  onSuccess={handlePaymentSuccess}
                  onError={setError}
                  userInfo={razorpayData ? {
                    name: sanitizeText(razorpayData.name),
                    email: sanitizeText(razorpayData.email),
                    contact: sanitizeText(razorpayData.contact)
                  } : null}
                />
              </div>
            </div>
          </div>
        )}
        
        <div className={`modal-footer ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
          <div className="secure-payment" style={{ 
            paddingLeft: isMobile ? "20px" : "50px", 
            transform: isMobile ? "translateX(-20px)" : "translateX(-50px)" 
          }}>
            <CreditCard size={isMobile ? 12 : 14} />
            <span>Secure payment processing by Razorpay</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditsModal;